export class FetchError extends Error {
  constructor(
    public res: Response,
    message: string | null | undefined = "No message",
  ) {
    const normalized = typeof message === "string" ? message.trim() : "";
    super(normalized || "No message");
  }
}

/** Backend sometimes returns empty / placeholder messages that are useless to show. */
export const isBlankErrorMessage = (message?: string | null) => {
  const normalized = message?.trim().toLowerCase();
  return !normalized || normalized === "no message";
};

export const getDisplayErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (error instanceof Error && !isBlankErrorMessage(error.message)) {
    return error.message;
  }
  return fallback;
};

export class HtmlResponseError extends Error {
  constructor() {
    super("HTML_RESPONSE");
  }
}

export const retryHandler = (
  failureCount: number,
  error: FetchError | Error,
) => {
  if (error instanceof HtmlResponseError) {
    return failureCount < 3;
  }

  if (error instanceof FetchError) {
    if (error.res.status >= 500 && failureCount < 3) {
      return true;
    } else {
      return false;
    }
  }

  if (failureCount < 3 && !error.message.includes("is not valid JSON")) {
    return true;
  }
  return false;
};

export const withRetry = async <T>(
  fn: () => Promise<T>,
  options?: {
    retry?: (failureCount: number, error: Error) => boolean;
    retryDelay?: (failureCount: number, error: Error) => number;
  },
): Promise<T> => {
  const retry = options?.retry ?? retryHandler;
  const retryDelay =
    options?.retryDelay ?? ((failureCount) => 1000 * failureCount);

  let failureCount = 0;
  while (true) {
    try {
      return await fn();
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (!retry(failureCount, err)) {
        throw err;
      }
      const delayMs = retryDelay(failureCount, err);
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
      failureCount += 1;
    }
  }
};
